import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Spinner } from 'react-bootstrap';
import {
  LuArrowDown,
  LuArrowUp,
  LuChevronRight,
  LuEye,
  LuPencil,
  LuTrash2,
} from 'react-icons/lu';

import FORMATTERS from 'helpers/formatters';
import { Card, CardBody, Dropdown, EmptyMessage, Tag } from '_components/_core';

import { ActionButton, RegistrationRow, StyledTable } from './styles';
import {
  CustomCard,
  CustomCardBody,
  CustomCardHeader,
} from '_components/Settings/components/SettingsV2/styles';
import { isEmpty } from 'lodash';

function EventRegistrations({
  registrations,
  isLoading,
  onDeleteRegistration,
  onEditRegistration,
  onViewRegistration,
  eventId,
  event,
  onFetchRegistrations,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [customFields, setCustomFields] = useState([]);

  // useEffect(() => {
  //   onFetchRegistrations(eventId);
  // }, [onFetchRegistrations, eventId]);

  // Reset to page 1 when search term changes
  // useEffect(() => {
  //   setCurrentPage(1);
  // }, [searchTerm]);

  const handleSort = useCallback(
    field => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection],
  );

  const sortedAndFilteredRegistrations = useMemo(() => {
    if (!registrations || !registrations.docs) return [];

    let filtered = [...registrations.docs];

    // Apply search filtering
    if (searchTerm.trim()) {
      const lowerSearch = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        reg =>
          reg.name?.toLowerCase().includes(lowerSearch) ||
          reg.email?.toLowerCase().includes(lowerSearch) ||
          reg.phone_number?.toLowerCase().includes(lowerSearch),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA = a[sortField];
      let valueB = b[sortField];

      // Handle nested properties with dot notation (e.g., "custom_fields.name")
      if (sortField.includes('.')) {
        const parts = sortField.split('.');
        valueA = parts.reduce((obj, key) => obj?.[key], a);
        valueB = parts.reduce((obj, key) => obj?.[key], b);
      }

      // Convert to strings for comparison if they exist
      valueA = valueA?.toString().toLowerCase() || '';
      valueB = valueB?.toString().toLowerCase() || '';

      // For dates, convert to timestamps
      if (sortField === 'created_at' || sortField === 'registered_at') {
        valueA = new Date(valueA).getTime() || 0;
        valueB = new Date(valueB).getTime() || 0;
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [registrations, searchTerm, sortField, sortDirection]);

  const paginatedRegistrations = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedAndFilteredRegistrations.slice(start, end);
  }, [sortedAndFilteredRegistrations, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(sortedAndFilteredRegistrations.length / pageSize);
  }, [sortedAndFilteredRegistrations, pageSize]);

  const SortIcon = useCallback(
    ({ field }) => {
      if (sortField !== field) return null;
      return sortDirection === 'asc' ? (
        <LuArrowUp size={14} />
      ) : (
        <LuArrowDown size={14} />
      );
    },
    [sortField, sortDirection],
  );

  const validCustomFields = useMemo(() => {
    const { custom_fields = [] } = event;

    return custom_fields.filter(
      field => field.type !== 'divider' && field.type !== 'heading',
    );
  }, [event]);

  const renderTableHeader = useCallback(() => {
    return (
      <thead>
        <tr>
          <th className="cursor-pointer" onClick={() => handleSort('created_at')}>
            <div className="d-flex align-items-center">
              Data e Hora Inscrição <SortIcon field="created_at" />
            </div>
          </th>
          {validCustomFields.map(field => (
            <th
              key={field.field_id}
              className="cursor-pointer"
              onClick={() => handleSort(field.name)}
            >
              <div className="d-flex align-items-center">
                {field.label} <SortIcon field={field.name} />
              </div>
            </th>
          ))}
          <th className="text-center">Status</th>
          <th className="text-right">Ações</th>
        </tr>
      </thead>
    );
  }, [handleSort, SortIcon, validCustomFields]);

  if (isLoading) {
    return (
      <Card>
        <CardBody className="d-flex justify-content-center align-items-center p-5">
          <Spinner animation="border" role="status">
            <span className="sr-only">Carregando...</span>
          </Spinner>
        </CardBody>
      </Card>
    );
  }

  if (isEmpty(registrations.data)) {
    return (
      <Card>
        <CardBody>
          <EmptyMessage
            title="Nenhuma inscrição encontrada"
            description="Este evento ainda não possui inscrições."
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <CustomCard>
            <CustomCardHeader>
              <h6 className="d-flex align-items-center mb-0">
                {event.name} <LuChevronRight className="ml-2 mr-2" />
                <span className="text-muted">Inscrições</span>
              </h6>
            </CustomCardHeader>
            <CustomCardBody noPadding>
              <div className="table-responsive">
                <StyledTable className="table">
                  {renderTableHeader()}
                  <tbody>
                    {registrations.data.map(registration => (
                      <RegistrationRow key={registration._id}>
                        <td>
                          {FORMATTERS.DATE_DDMMYYYYHHMMSS(registration.created_at) || '-'}
                        </td>
                        {validCustomFields.map(field => (
                          <td key={field.field_id}>
                            {registration.form_data[field.name] || '-'}
                          </td>
                        ))}
                        <td className="text-center">
                          {registration.status === 'waiting_list' ? (
                            <Tag bg="warning">Lista de espera</Tag>
                          ) : (
                            <Tag bg="success">Confirmado</Tag>
                          )}
                        </td>
                        <td className="text-right">
                          <Dropdown
                            placement="bottom-end"
                            options={[
                              {
                                label: 'Visualizar',
                                icon: <LuEye />,
                                onClick: () => onViewRegistration(registration),
                                disabled: false,
                              },
                              {
                                label: 'Editar',
                                icon: <LuPencil />,
                                onClick: () => onEditRegistration(registration),
                                disabled: false,
                              },
                              {
                                label: 'Excluir',
                                icon: <LuTrash2 className="text-danger" />,
                                onClick: () => onDeleteRegistration(registration),
                                className: 'text-danger',
                              },
                            ]}
                          >
                            <ActionButton variant="white">Ações</ActionButton>
                          </Dropdown>
                        </td>
                      </RegistrationRow>
                    ))}
                  </tbody>
                </StyledTable>
              </div>
            </CustomCardBody>
          </CustomCard>
        </Col>
      </Row>
    </>
  );
}

EventRegistrations.propTypes = {
  registrations: PropTypes.shape({
    docs: PropTypes.array,
    total: PropTypes.number,
    limit: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  onEditRegistration: PropTypes.func.isRequired,
  onViewRegistration: PropTypes.func.isRequired,
  onFetchRegistrations: PropTypes.func.isRequired,
  onUpdateRegistration: PropTypes.func.isRequired,
  onDeleteRegistration: PropTypes.func.isRequired,
};

export default EventRegistrations;
